<template>
  <div class="flex flex-col gap-2">
    <button
      :id="fieldContext?.inputId"
      ref="dropzoneRef"
      type="button"
      :disabled="isDisabled || isAtMax"
      :class="[
        `flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm transition`,
        `outline-2 -outline-offset-1 outline-blue-500 focus-visible:outline`,
        isDropping
          ? `border-green-300 bg-green-100 dark:border-green-500/20 dark:bg-green-500/20`
          : `border-border text-muted-foreground hover:bg-gray-100 dark:hover:bg-card`,
        (isDisabled || isAtMax) && `pointer-events-none opacity-50`,
        fieldContext?.validationStatus.value === 'error'
          ? `border-red-300 outline-red-500 dark:border-red-800`
          : `outline-blue-500`,
      ]"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      @click="openFilePicker"
    >
      <ArrowUpTrayIcon class="size-4 shrink-0" />

      <span>{{ triggerLabel }}</span>
    </button>

    <ul v-if="modelValue.length > 0" class="flex flex-col gap-1">
      <li
        v-for="(file, index) in modelValue"
        :key="index"
        class="flex items-center gap-2 rounded-md px-2 py-1 text-sm"
      >
        <DocumentIcon class="size-4 shrink-0 text-muted-foreground" />
        <span class="min-w-0 grow truncate text-foreground">
          {{ file.name }}
        </span>

        <span aria-hidden="true" class="shrink-0 text-xs text-muted-foreground">
          {{ formatFileSize(file.size) }}
        </span>

        <button
          type="button"
          :aria-label="`Remove ${file.name}`"
          :disabled="isDisabled"
          class="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none dark:hover:bg-card"
          @click="removeFile(index)"
        >
          <XMarkIcon class="size-3.5" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

import ArrowUpTrayIcon from "@/icons/heroicons/arrow-up-tray-mini.svg";
import DocumentIcon from "@/icons/heroicons/document-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";
import { useDropzone, useFilePicker } from "@/lib/composables/dropzone";

import { injectFieldContext } from "./context";

const modelValue = defineModel<File[]>({ default: () => [] });

const props = withDefaults(
  defineProps<{
    accept?: string[];
    max?: number;
    disabled?: boolean;
  }>(),
  {
    accept: () => [],
    max: 1,
  }
);

const dropzoneRef = ref<HTMLElement | null>(null);
const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const isAtMax = computed(() => {
  return modelValue.value.length >= props.max;
});

const hasMax = computed(() => Number.isFinite(props.max));

const triggerLabel = computed(() => {
  if (modelValue.value.length === 0) {
    return props.max === 1 ? "Choose a file" : "Choose files";
  }

  const remaining = props.max - modelValue.value.length;
  if (remaining <= 0) {
    return `Maximum files reached`;
  }

  if (!hasMax.value) {
    return `Add more files`;
  }

  return `Add ${remaining === 1 ? "another file" : "more files"} (${
    modelValue.value.length
  }/${props.max})`;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const addFiles = (files: File[] | null) => {
  if (!files || isDisabled.value) return;

  const remaining = props.max - modelValue.value.length;
  if (remaining <= 0) return;

  modelValue.value = [...modelValue.value, ...files.slice(0, remaining)];
};

const removeFile = (index: number) => {
  modelValue.value = modelValue.value.filter((_, i) => i !== index);
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const { isDropping } = useDropzone(dropzoneRef, {
  accept: () => props.accept,
  multiple: () => modelValue.value.length + 1 < props.max,
  onDrop: addFiles,
});

const { open: openFilePicker } = useFilePicker({
  accept: () => props.accept,
  multiple: () => props.max - modelValue.value.length > 1,
  onDrop: addFiles,
});
</script>
